import type { SuperBoostRuleSet } from "./types.js";

// super-boost ruleset for FY2025-26 (1 July 2025 – 30 June 2026).
//
// Every figure is transcribed from the verification ledgers under
// docs/research/discovery/verification/ with its source + the per-figure
// correction the data-critic applied. CITED current-FY values are used; the
// imprecise scout values flagged in the ledgers are NOT (see notes below).
//
// Never edit a shipped ruleset to "fix" a later year — author a new
// fyXXXX-XX.ts and register it. Co-contribution thresholds, the concessional
// cap, the TSB caps and (eventually) LISTO all reprice; re-verify each rollover.
export const fy2025_26: SuperBoostRuleSet = {
  engine: "super-boost",
  fy: "2025-26",
  effectiveFrom: "2025-07-01",
  effectiveTo: "2026-06-30",
  sources: {
    coContribution:
      "https://www.ato.gov.au/individuals-and-families/super-for-individuals-and-families/super/growing-and-keeping-track-of-your-super/how-to-save-more-in-your-super/government-super-contributions/super-co-contribution",
    coContributionRates:
      "https://www.ato.gov.au/tax-rates-and-codes/key-superannuation-rates-and-thresholds/government-contributions",
    spouseOffset:
      "https://www.ato.gov.au/individuals-and-families/super-for-individuals-and-families/super/growing-and-keeping-track-of-your-super/how-to-save-more-in-your-super/spouse-super-contributions",
    spouseOffsetT3:
      "https://www.ato.gov.au/forms-and-instructions/individual-supplementary-tax-return-2025-instructions/tax-offset-questions-t3-t9-supplementary-tax-return-2025/t3-super-contributions-on-behalf-of-your-spouse-2025",
    concessionalCap:
      "https://www.ato.gov.au/tax-rates-and-codes/key-superannuation-rates-and-thresholds/contributions-caps",
    carryForward:
      "https://www.ato.gov.au/individuals-and-families/super-for-individuals-and-families/super/growing-and-keeping-track-of-your-super/caps-limits-and-tax-on-super-contributions/concessional-contributions-cap",
    transferBalanceCap:
      "https://www.ato.gov.au/tax-rates-and-codes/key-superannuation-rates-and-thresholds/transfer-balance-cap",
    listo:
      "https://www.ato.gov.au/individuals-and-families/super-for-individuals-and-families/super/growing-and-keeping-track-of-your-super/how-to-save-more-in-your-super/government-super-contributions/low-income-super-tax-offset",
  },
  sourcesVerifiedAt: "2026-06-22",

  // Government co-contribution (co-contribution ledger #1–#6, all CITED):
  //   max $500, 50c/$1 match; lower threshold $47,488; higher (cut-out) $62,488;
  //   taper 3.333c/$1 over the lower threshold ($15,000 window × 0.03333 ≈ $500,
  //   self-reconciling); under 71 at year end; TSB < $2.0m general transfer
  //   balance cap (2025-26) at the prior 30 June.
  coContribution: {
    maxAmount: 500,
    matchingRate: 0.5,
    lowerThreshold: 47_488,
    higherThreshold: 62_488,
    taperRate: 0.03333,
    maxAgeExclusive: 71,
    tsbCap: 2_000_000,
  },

  // Spouse-contribution tax offset (spouse ledger #1–#6, all CITED):
  //   18% × lesser of [$3,000 − (spouseIncome − $37,000)] and [contribution];
  //   max $540; full offset ≤ $37,000; nil at/above $40,000; spouse-side TSB <
  //   $2.0m general transfer balance cap.
  spouseOffset: {
    offsetRate: 0.18,
    maxContributionBase: 3_000,
    fullOffsetThreshold: 37_000,
    cutOutThreshold: 40_000,
    tsbCap: 2_000_000,
  },

  // Concessional cap + in-fund tax (carry-forward ledger #1, sacrifice ledger
  // #1–#2, both CITED): $30,000 general cap for 2024-25 and 2025-26; 15% flat
  // in-fund concessional tax.
  concessionalCap: 30_000,
  concessionalTaxRate: 0.15,

  // Carry-forward (carry-forward ledger #4–#5, cap-expiry ledger #1–#3, CITED):
  //   up to 5 previous FYs; unused expires after 5 years (oldest used first);
  //   first accrual year 2018-19; $500k TSB gate at the prior 30 June (unindexed).
  carryForward: {
    windowYears: 5,
    firstAccrualFy: "2018-19",
    tsbGate: 500_000,
  },

  // LISTO (sacrifice ledger #5, CITED): 15% of concessional contributions, max
  // $500, for adjusted taxable income ≤ $37,000 (2025-26). Threshold rises to
  // $45,000 / max $810 from 1 Jul 2027 — a future FY ruleset, not this one.
  listo: {
    max: 500,
    incomeThreshold: 37_000,
    rate: 0.15,
  },
};

// ---------------------------------------------------------------------------
// Concessional-cap series — the per-FY general cap, needed to derive unused
// carry-forward headroom for years the caller may not have a cap figure for.
// Lives beside the rulesets (shared across FY versions; the caller may also
// pass an explicit per-year cap in the history, which always wins).
//
// Cap history (carry-forward ledger #1–#3, cap-expiry ledger #5, CITED via
// Treasury + ATO primaries):
//   2017-18 → 2020-21 : $25,000   (first indexation step at 1 Jul 2021)
//   2021-22 → 2023-24 : $27,500
//   2024-25 & 2025-26 : $30,000   (step at 1 Jul 2024)
//   2026-27           : $32,500   (AWOTE indexation at 1 Jul 2026)
// ---------------------------------------------------------------------------
export const CONCESSIONAL_CAP_SERIES: Record<string, number> = {
  "2017-18": 25_000,
  "2018-19": 25_000,
  "2019-20": 25_000,
  "2020-21": 25_000,
  "2021-22": 27_500,
  "2022-23": 27_500,
  "2023-24": 27_500,
  "2024-25": 30_000,
  "2025-26": 30_000,
  "2026-27": 32_500,
};
